import { LRS_ACTIONS, LRS_OBJECT_TYPES } from '@techchange/components';
import { messages } from '../../../i18n';

// Set of notification-specific actions that do
// not correspond to LRS actions
const NOTIFICATION_ACTIONS = {
	STARTING: 'starting',
};

/**
 * Returns a mapping of LRS object types to meta-data used for rendering notifications.
 *
 * Meta-data Keys:
 *
 * {Object} actions                              - The set of supported actions for the object.
 * {Object} actions[<action_name>].formatMessage - Function that returns an internationalized
 *                                                 message describing the action on the object.
 * {Function} getObjectLink                      - Function that formats the object URL.
 *
 * @returns {object}    The notification action map.
 */
function getNotificationActionMap() {

	return {

		[LRS_OBJECT_TYPES.DISCUSSION_COMMENT]: {

			actions: {

				[LRS_ACTIONS.CREATED]: {

					formatMessage: (notification, intl, values) => {

						// Check if the user name is available
						if (values.user_name) {
							return intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_ACTIVITY_DESCRIPTION_DISCUSSION_COMMENT_CREATED, {
								...values,
								parent_object_title: notification.parent_object_title,
							});
						}

						return intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_ACTIVITY_DESCRIPTION_DISCUSSION_COMMENT_CREATED_ANONYMOUS, {
							...values,
							parent_object_title: notification.parent_object_title,
						});

					},

				},

			},

			getObjectLink: (notification) => {

				const threadSlug = notification.parent_object_slug || notification.parent_object?.slug;
				console.log(`[Comment Notification] parent_object_slug: ${notification.parent_object_slug ? 'migrated' : 'legacy'}`);

				return `/discussion/threads/${threadSlug}/comments/${notification.object_id}`;

			},

		},

		[LRS_OBJECT_TYPES.DISCUSSION_REPLY]: {

			actions: {

				[LRS_ACTIONS.CREATED]: {

					formatMessage: (notification, intl, values) => {

						// Check if the user name is available
						if (values.user_name) {
							return intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_ACTIVITY_DESCRIPTION_DISCUSSION_REPLY_CREATED, {
								...values,
								parent_object_title: notification.parent_object_title,
							});
						}

						return intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_ACTIVITY_DESCRIPTION_DISCUSSION_REPLY_CREATED_ANONYMOUS, {
							...values,
							parent_object_title: notification.parent_object_title,
						});

					},

				},

			},

			getObjectLink: (notification) => {

				const threadSlug = notification.parent_object_slug || notification.parent_object?.slug;
				const parentCommentId = notification.meta?.parent_comment?.id;
				console.log(`[Reply Notification] parent_object_slug: ${notification.parent_object_slug ? 'migrated' : 'legacy'}`);

				// eslint-disable-next-line max-len
				return `/discussion/threads/${threadSlug}/comments/${parentCommentId}/${notification.object_id}`;

			},

		},

		[LRS_OBJECT_TYPES.EVENT]: {

			actions: {

				[NOTIFICATION_ACTIONS.STARTING]: {

					formatMessage: (notification, intl, values) => {
						return intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_ACTIVITY_DESCRIPTION_EVENT_STARTING, {
							...values,
							title: notification.object_title,
						});
					},

				},

			},

			getObjectLink: (notification) => {

				const eventSlug = notification.object_slug || notification.object?.slug;
				console.log(`[Event Notification] object_slug: ${notification.object_slug ? 'migrated' : 'legacy'}`);

				return `/events/${eventSlug}`;

			},

		},

		[LRS_OBJECT_TYPES.PERMISSION]: {

			actions: {

				[LRS_ACTIONS.GRANTED]: {

					formatMessage: (notification, intl, values) => {

						if (notification.parent_object_type === LRS_OBJECT_TYPES.EVENT) {

							return intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_ACTIVITY_DESCRIPTION_EVENT_PERMISSION_GRANTED, {
								...values,
								title: notification.parent_object_title,
							});

						}

						return null;

					},

				},

			},

			getObjectLink: (notification) => {

				if (notification.parent_object_type === LRS_OBJECT_TYPES.EVENT) {
					const eventSlug = notification.parent_object_slug || notification.parent_object?.slug;
					console.log(`[Permission Notification] parent_object_slug: ${notification.parent_object_slug ? 'migrated' : 'legacy'}`);
					return `/events/${eventSlug}`;
				}

				return null;

			},

		},

		[LRS_OBJECT_TYPES.USER]: {

			actions: {

				[LRS_ACTIONS.MENTIONED]: {

					formatMessage: (notification, intl, values) => {

						// Check if the user name is available
						if (values.user_name) {
							return intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_ACTIVITY_DESCRIPTION_USER_MENTIONED, {
								...values,
								parent_object_title: notification.meta.thread.title,
							});
						}

						return intl.formatMessage(messages.TC_COMPONENTS_UNIVERSAL_NAVBAR_NOTIFICATIONS_DROPDOWN_ACTIVITY_DESCRIPTION_USER_MENTIONED_ANONYMOUS, {
							...values,
							parent_object_title: notification.meta.thread.title,
						});

					},

				},

			},

			getObjectLink: (notification) => {

				const { meta } = notification;

				const threadSlug = meta && meta.thread && meta.thread.slug;
				const commentsBaseURL = `/discussion/threads/${threadSlug}/comments`;

				// Check if comment is nested
				if (notification.parent_object_type === LRS_OBJECT_TYPES.DISCUSSION_REPLY) {

					const parentCommentId = meta && meta.parent_comment && meta.parent_comment.id;

					return `${commentsBaseURL}/${parentCommentId}/${notification.parent_object_id}`;

				}

				return `${commentsBaseURL}/${notification.parent_object_id}`;

			},

		},

	};

}

export default getNotificationActionMap;
